<template>
  <div class="operations-page">
    <header class="ops-header">
      <div>
        <h1>Dashboard · Operaciones</h1>
        <p>Resumen mensual de trámites y entregas</p>
      </div>
      <form
        class="ops-filters"
        aria-label="Filtros del dashboard"
        @submit.prevent="load"
      >
        <label class="ops-filter ops-filter--distributor"
          >Distribuidor
          <select
            v-model="filters.distId"
            aria-label="Distribuidor"
            @change="changeFilters"
          >
            <option value="">Todos los distribuidores</option>
            <option
              v-for="d in distributors"
              :key="d.id"
              :value="String(d.id)"
              >{{ d.name }}</option
            >
          </select>
        </label>
        <label class="ops-filter"
          >Mes
          <select
            v-model="filters.month"
            aria-label="Mes"
            @change="changeFilters"
          >
            <option
              v-for="(month, index) in months"
              :key="month"
              :value="index + 1"
              >{{ month }}</option
            >
          </select>
        </label>
        <label class="ops-filter ops-filter--year"
          >Año
          <select
            v-model="filters.year"
            aria-label="Año"
            @change="changeFilters"
          >
            <option v-for="year in years" :key="year" :value="year">{{
              year
            }}</option>
          </select>
        </label>
        <button
          class="ops-icon-button"
          type="submit"
          :disabled="loading"
          aria-label="Actualizar dashboard"
          title="Actualizar dashboard"
        >
          <v-icon aria-hidden="true">refresh</v-icon>
        </button>
      </form>
    </header>

    <div class="ops-report-action">
      <span
        >Informe completo del período: resumen, equipo y detalle de
        registros.</span
      >
      <button
        class="ops-button ops-export-button"
        :disabled="!report || loading || exporting || !!error"
        :aria-busy="String(exporting)"
        @click="downloadReport"
      >
        <v-icon aria-hidden="true">{{
          exporting ? 'hourglass_empty' : 'picture_as_pdf'
        }}</v-icon>
        {{ exporting ? 'Preparando PDF…' : 'Descargar informe PDF' }}
      </button>
    </div>
    <p v-if="exporting" class="ops-export-status" role="status">
      Preparando todos los registros del período. Puedes seguir consultando el
      dashboard.
    </p>
    <div v-if="exportError" class="ops-error" role="alert">
      <v-icon aria-hidden="true">error_outline</v-icon>
      <span>{{ exportError }}</span>
      <button
        class="ops-button"
        :disabled="loading || exporting || !!error"
        @click="downloadReport"
      >
        Reintentar descarga
      </button>
    </div>

    <div v-if="error" class="ops-error" role="alert">
      <v-icon aria-hidden="true">error_outline</v-icon>
      <span>{{ error }}</span>
      <button class="ops-button" :disabled="loading" @click="load">
        Reintentar
      </button>
    </div>
    <div
      v-if="!report && loading"
      class="ops-skeleton"
      role="status"
      aria-label="Cargando dashboard"
    >
      <div v-for="n in 5" :key="n" class="ops-skeleton-card"></div>
      <div class="ops-skeleton-panel"></div>
      <div class="ops-skeleton-panel"></div>
      <span class="ops-sr-only">Cargando indicadores de operaciones…</span>
    </div>

    <template v-if="report">
      <div class="ops-period" role="status">
        <span
          >{{ periodLabel }} · Corte al {{ formatDate(report.period.asOf)
          }}<span v-if="loading"> · Actualizando…</span></span
        >
        <button
          class="ops-text-button"
          :aria-expanded="String(showAlerts)"
          aria-controls="ops-alerts"
          @click="showAlerts = !showAlerts"
        >
          <v-icon small aria-hidden="true">notifications_none</v-icon
          >{{ report.alerts.length }} avisos
        </button>
      </div>
      <section
        v-if="showAlerts"
        id="ops-alerts"
        class="ops-panel ops-alerts"
        aria-label="Avisos de operaciones"
      >
        <h2>Avisos del período</h2>
        <p v-if="!report.alerts.length" class="ops-empty">
          No hay alertas registradas en este período.
        </p>
        <ul v-else>
          <li v-for="alert in report.alerts" :key="alert.id">
            <v-icon
              :class="
                alert.kind === 'delivered' ? 'ops-success' : 'ops-warning'
              "
              aria-hidden="true"
              >{{
                alert.kind === 'delivered' ? 'check_circle' : 'schedule'
              }}</v-icon
            >
            <span
              >{{ alertText(alert)
              }}<small>{{ formatDate(alert.occurredAt, true) }}</small></span
            >
            <router-link
              v-if="alert.shipmentId && report.canEditLogistics"
              :to="'/logistica/servicio/' + alert.shipmentId"
              >Ver servicio</router-link
            >
          </li>
        </ul>
      </section>

      <section
        class="ops-metrics"
        aria-label="Indicadores de trámites"
        :aria-busy="String(loading)"
      >
        <button
          v-for="metric in metrics"
          :key="metric.key"
          class="ops-metric"
          :class="'ops-metric--' + metric.tone"
          @click="openList(metric.key, metric.title)"
        >
          <span class="ops-metric-icon"
            ><v-icon aria-hidden="true">{{ metric.icon }}</v-icon></span
          >
          <span class="ops-metric-content">
            <span class="ops-metric-title">{{ metric.title }}</span>
            <strong>{{ number(report.totals[metric.key]) }}</strong>
            <span class="ops-metric-description">{{ metric.description }}</span>
            <span class="ops-metric-link"
              >Ver lista
              <v-icon small aria-hidden="true">arrow_forward</v-icon></span
            >
          </span>
        </button>
      </section>

      <div class="ops-two-columns ops-pending-grid">
        <section class="ops-panel">
          <div class="ops-section-heading">
            <v-icon aria-hidden="true">person</v-icon>
            <div>
              <h2>Pendientes por responsables</h2>
              <p>Quién debe aportar la información pendiente</p>
            </div>
          </div>
          <div class="ops-bars">
            <button
              v-for="(r, index) in report.responsible"
              :key="r.name"
              class="ops-bar-row"
              @click="
                openList('pending', 'Pendientes · ' + r.name, {
                  responsible: r.name,
                })
              "
            >
              <span class="ops-bar-label">{{ r.name }}</span
              ><span class="ops-bar-track" aria-hidden="true"
                ><span
                  :class="'ops-bar-fill ops-bar-fill--' + index"
                  :style="{ width: barWidth(r.total, report.responsible) }"
                ></span></span
              ><strong>{{ number(r.total) }}</strong>
            </button>
          </div>
          <p class="ops-footnote">
            Un trámite puede tener pendientes con ambos responsables. Selecciona
            «Sin asignar» para completar el responsable que falta.
          </p>
        </section>
        <section class="ops-panel">
          <div class="ops-section-heading">
            <v-icon aria-hidden="true">pie_chart</v-icon>
            <div>
              <h2>Pendientes por motivo</h2>
              <p>Documentos y correcciones por resolver</p>
            </div>
          </div>
          <div v-if="report.pendingReasons.length" class="ops-bars ops-reasons">
            <button
              v-for="(r, index) in report.pendingReasons"
              :key="r.id"
              class="ops-bar-row"
              @click="openList('pending', r.name, { reasonId: r.id })"
            >
              <span class="ops-bar-label">{{ r.name }}</span
              ><span class="ops-bar-track" aria-hidden="true"
                ><span
                  :class="'ops-bar-fill ops-bar-fill--' + (index % 6)"
                  :style="{ width: barWidth(r.total, report.pendingReasons) }"
                ></span></span
              ><strong>{{ number(r.total) }}</strong>
            </button>
          </div>
          <p v-else class="ops-empty">
            No hay motivos pendientes registrados para este corte.
          </p>
          <p class="ops-footnote">
            Se muestran los documentos sin resolver, aunque el trámite ya esté
            aprobado.
          </p>
        </section>
      </div>

      <section class="ops-panel ops-sla-panel" aria-labelledby="ops-sla-title">
        <div class="ops-sla-main">
          <div class="ops-section-heading">
            <v-icon aria-hidden="true">timer</v-icon>
            <div>
              <h2 id="ops-sla-title">Estado de SLA</h2>
              <p>Trámites abiertos y resueltos durante el mes</p>
            </div>
          </div>
          <div class="ops-sla-states">
            <button
              class="ops-sla-state ops-sla-state--success"
              @click="openList('within', 'Trámites dentro del KPI')"
            >
              <span>Dentro del KPI</span
              ><strong>{{ number(report.totals.within) }}</strong
              ><small>En plazo · hasta 3 días hábiles</small>
            </button>
            <button
              class="ops-sla-state ops-sla-state--warning"
              @click="openList('warning', 'Trámites próximos a vencer')"
            >
              <span>Próximos a vencer</span
              ><strong>{{ number(report.totals.warning) }}</strong
              ><small>Abiertos · días 2 y 3</small>
            </button>
            <button
              class="ops-sla-state ops-sla-state--danger"
              @click="openList('overdue', 'Trámites fuera del KPI')"
            >
              <span>Fuera del KPI</span
              ><strong>{{ number(report.totals.overdue) }}</strong
              ><small>Más de 3 días hábiles</small>
            </button>
          </div>
          <button
            v-if="report.totals.warning"
            class="ops-preventive"
            @click="openList('warning', 'Trámites próximos a vencer')"
          >
            <v-icon small aria-hidden="true">warning</v-icon>Alerta preventiva:
            {{ number(report.totals.warning) }} trámite(s) requieren atención
            antes de superar el plazo.<v-icon small aria-hidden="true"
              >chevron_right</v-icon
            >
          </button>
          <p v-else class="ops-sla-clear">
            <v-icon small aria-hidden="true">check_circle_outline</v-icon>No hay
            trámites próximos a vencer en este corte.
          </p>
        </div>
        <aside class="ops-sla-info">
          <h3>
            <v-icon small aria-hidden="true">info_outline</v-icon>Cómputo del
            SLA
          </h3>
          <p>
            Desde la recepción en TRANSIT. Solo días laborables; se excluyen
            fines de semana y feriados.
          </p>
          <p>
            Alerta desde el día 2. El día 3 está dentro del plazo. Cada
            reingreso inicia un nuevo ciclo.
          </p>
          <small>SLA: Acuerdo de Nivel de Servicio.</small>
        </aside>
      </section>

      <div class="ops-two-columns">
        <section
          v-for="service in report.logistics"
          :key="service.type"
          class="ops-panel ops-logistics"
        >
          <div class="ops-section-heading">
            <v-icon aria-hidden="true">{{
              service.type === 'mensajero' ? 'mail_outline' : 'local_shipping'
            }}</v-icon>
            <div>
              <h2>
                {{
                  service.type === 'mensajero' ? 'Mensajería' : 'Transportista'
                }}
              </h2>
            </div>
          </div>
          <div class="ops-delivery-counts">
            <button
              @click="
                openList(
                  service.type + '-delivered',
                  serviceTitle(service.type) + ' · Entregados'
                )
              "
            >
              <span>Entregados en el mes</span
              ><strong class="ops-success">{{
                number(service.delivered)
              }}</strong>
            </button>
            <button
              @click="
                openList(
                  service.type + '-pending',
                  serviceTitle(service.type) + ' · Pendientes'
                )
              "
            >
              <span>Pendientes de entrega</span
              ><strong>{{ number(service.pending) }}</strong
              ><small
                >{{ number(service.enRoute) }} en camino / en punto de
                entrega</small
              >
            </button>
          </div>
          <p class="ops-delivery-note">
            <v-icon small aria-hidden="true">notifications_active</v-icon>Aviso
            al confirmar la entrega al cliente
          </p>
        </section>
      </div>
      <p v-if="report.unlinked" class="ops-data-note">
        {{ number(report.unlinked) }} servicios sin distribuidor asignado: se
        incluyen al seleccionar «Todos los distribuidores». Puedes asignarlo
        desde sus listas.
      </p>

      <section class="ops-panel ops-team" aria-labelledby="ops-team-title">
        <div class="ops-section-heading ops-team-heading">
          <v-icon aria-hidden="true">group</v-icon>
          <div>
            <h2 id="ops-team-title">Desempeño del equipo</h2>
            <p>
              {{ number(report.team.length) }} colaboradores · ordena y compara
              su actividad
            </p>
          </div>
          <label class="ops-team-search"
            ><span class="ops-sr-only">Buscar colaborador</span
            ><input
              v-model="teamSearch"
              type="search"
              placeholder="Buscar colaborador"
          /></label>
        </div>
        <div class="ops-team-controls">
          <label
            >Ordenar por
            <select v-model="teamSort" aria-label="Ordenar equipo por">
              <option
                v-for="column in teamColumns"
                :key="column.key"
                :value="column.key"
                >{{ column.label }}</option
              >
            </select>
          </label>
          <button
            class="ops-button ops-sort-direction"
            :aria-label="
              teamDirection === 'desc'
                ? 'Cambiar a orden ascendente'
                : 'Cambiar a orden descendente'
            "
            @click="teamDirection = teamDirection === 'desc' ? 'asc' : 'desc'"
          >
            <v-icon small aria-hidden="true">{{
              teamDirection === 'desc' ? 'arrow_downward' : 'arrow_upward'
            }}</v-icon
            >{{ teamDirection === 'desc' ? 'Mayor a menor' : 'Menor a mayor' }}
          </button>
          <label class="ops-page-size"
            >Mostrar
            <select
              v-model.number="teamPageSize"
              aria-label="Colaboradores por página"
              ><option :value="5">5 por página</option
              ><option :value="10">10 por página</option
              ><option :value="20">20 por página</option></select
            >
          </label>
        </div>
        <div
          class="ops-table-scroll"
          role="region"
          aria-label="Desempeño de colaboradores"
          tabindex="0"
        >
          <table class="ops-table ops-team-table">
            <thead>
              <tr>
                <th
                  v-for="column in teamColumns"
                  :key="column.key"
                  scope="col"
                  :aria-sort="
                    teamSort === column.key
                      ? teamDirection === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  "
                >
                  <button
                    :aria-label="'Ordenar por ' + column.label"
                    :class="{ 'ops-sort-active': teamSort === column.key }"
                    @click="sortTeam(column.key)"
                  >
                    {{ column.label
                    }}<v-icon small aria-hidden="true">{{
                      teamSort === column.key
                        ? teamDirection === 'asc'
                          ? 'arrow_upward'
                          : 'arrow_downward'
                        : 'unfold_more'
                    }}</v-icon>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(member, index) in pagedTeam" :key="member.id">
                <th scope="row">
                  <div class="ops-person">
                    <span
                      class="ops-avatar"
                      :class="'ops-avatar--' + (index % 4)"
                      aria-hidden="true"
                      >{{ initials(member.name) }}</span
                    ><span
                      >{{ member.name }}<small>{{ member.role }}</small></span
                    >
                  </div>
                </th>
                <td
                  data-label="En proceso"
                  :class="{ 'ops-sorted-cell': teamSort === 'processing' }"
                >
                  {{ number(member.processing) }}
                </td>
                <td
                  data-label="Pendientes"
                  :class="{ 'ops-sorted-cell': teamSort === 'pending' }"
                >
                  {{ number(member.pending) }}
                </td>
                <td
                  data-label="Aprobados"
                  :class="{ 'ops-sorted-cell': teamSort === 'approved' }"
                >
                  {{ number(member.approved) }}
                </td>
                <td
                  data-label="Rechazados"
                  :class="{ 'ops-sorted-cell': teamSort === 'denied' }"
                >
                  {{ number(member.denied) }}
                </td>
                <td
                  data-label="Entregas"
                  :class="{ 'ops-sorted-cell': teamSort === 'delivered' }"
                >
                  {{ number(member.delivered) }}
                </td>
                <td
                  data-label="Fuera de SLA"
                  :class="[
                    member.overdue ? 'ops-danger' : 'ops-success',
                    { 'ops-sorted-cell': teamSort === 'overdue' },
                  ]"
                >
                  {{ number(member.overdue) }}
                </td>
              </tr>
              <tr v-if="!filteredTeam.length">
                <td colspan="7" class="ops-empty">
                  {{
                    teamSearch
                      ? 'No hay colaboradores que coincidan con la búsqueda.'
                      : 'No hay colaboradores activos.'
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <nav class="ops-team-pagination" aria-label="Paginación del equipo">
          <span role="status">{{ teamRange }}</span>
          <div>
            <button
              class="ops-icon-button"
              aria-label="Página anterior del equipo"
              :disabled="teamPage <= 1"
              @click="teamPage -= 1"
            >
              <v-icon aria-hidden="true">chevron_left</v-icon></button
            ><span>Página {{ teamPage }} de {{ teamPages }}</span
            ><button
              class="ops-icon-button"
              aria-label="Página siguiente del equipo"
              :disabled="teamPage >= teamPages"
              @click="teamPage += 1"
            >
              <v-icon aria-hidden="true">chevron_right</v-icon>
            </button>
          </div>
        </nav>
        <p class="ops-footnote">
          En proceso, pendientes y SLA: creador del trámite. Aprobaciones,
          rechazos y entregas: usuario que registró la acción. Los registros
          históricos sin autor no se atribuyen a otro colaborador.
        </p>
      </section>

      <v-dialog
        :value="!!detail"
        max-width="1180"
        :fullscreen="$vuetify.breakpoint.xsOnly"
        :persistent="saving !== null"
        content-class="ops-modal-shell"
        @input="onDetailVisibility"
      >
        <section
          v-if="detail"
          ref="detailPanel"
          class="operations-page ops-panel ops-detail"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          aria-labelledby="ops-detail-title"
          aria-describedby="ops-detail-context"
        >
          <div class="ops-detail-heading">
            <span class="ops-detail-symbol" aria-hidden="true"
              ><v-icon>{{
                isLogisticsDetail ? 'local_shipping' : 'assignment'
              }}</v-icon></span
            >
            <div>
              <h2 id="ops-detail-title">{{ detail.title }}</h2>
              <p id="ops-detail-context">
                {{ periodLabel }} ·
                {{
                  distributorName(report.period.distId) === 'Sin asignar'
                    ? 'Todos los distribuidores'
                    : distributorName(report.period.distId)
                }}
              </p>
              <span class="ops-detail-count">{{
                detailLoading
                  ? 'Consultando registros…'
                  : number(detail.total) + ' registros'
              }}</span>
            </div>
            <button
              ref="detailClose"
              class="ops-icon-button"
              aria-label="Cerrar lista"
              :disabled="saving !== null"
              @click="closeList"
            >
              <v-icon aria-hidden="true">close</v-icon>
            </button>
          </div>
          <div class="ops-detail-body">
            <p v-if="detailError" class="ops-error" role="alert">
              {{ detailError
              }}<button class="ops-button" @click="loadDetail">
                Reintentar
              </button>
            </p>
            <p v-if="saveError" class="ops-error" role="alert">
              {{ saveError }}
            </p>
            <p v-if="detailLoading" role="status" class="ops-empty">
              Cargando lista…
            </p>
            <div
              v-else
              class="ops-table-scroll"
              tabindex="0"
              role="region"
              :aria-label="detail.title"
            >
              <table class="ops-table ops-detail-table">
                <thead>
                  <tr>
                    <th scope="col">
                      {{ isLogisticsDetail ? 'Servicio' : 'Trámite' }}
                    </th>
                    <th scope="col">
                      {{ isLogisticsDetail ? 'Cliente' : 'Distribuidor' }}
                    </th>
                    <th scope="col">
                      {{ detail.section === 'pending' ? 'Motivo' : 'Estado' }}
                    </th>
                    <th scope="col">
                      {{ isLogisticsDetail ? 'Distribuidor' : 'Días hábiles' }}
                    </th>
                    <th v-if="detail.section === 'pending'" scope="col">
                      Responsable del pendiente
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in detail.items"
                    :key="row.pendingId || row.id"
                  >
                    <td
                      class="ops-detail-reference"
                      :data-label="isLogisticsDetail ? 'Servicio' : 'Trámite'"
                    >
                      <router-link
                        v-if="!isLogisticsDetail && canOpenInvoice"
                        :to="'/facturas/editar/' + row.id"
                        >{{ row.invoiceNumber }}</router-link
                      ><router-link
                        v-else-if="isLogisticsDetail && report.canEditLogistics"
                        :to="'/logistica/servicio/' + row.id"
                        >{{ row.trackingNumber }}</router-link
                      ><span v-else>{{
                        isLogisticsDetail
                          ? row.trackingNumber
                          : row.invoiceNumber
                      }}</span
                      ><small v-if="!isLogisticsDetail">{{
                        row.laboratory
                      }}</small>
                    </td>
                    <td
                      :data-label="
                        isLogisticsDetail ? 'Cliente' : 'Distribuidor'
                      "
                    >
                      {{ isLogisticsDetail ? row.clientName : row.distributor }}
                    </td>
                    <td
                      :data-label="
                        detail.section === 'pending' ? 'Motivo' : 'Estado'
                      "
                      :class="{
                        'ops-detail-wide': detail.section === 'pending',
                      }"
                    >
                      <span
                        :class="{
                          'ops-state-tag': detail.section !== 'pending',
                        }"
                        >{{
                          detail.section === 'pending'
                            ? row.reason
                            : stateLabel(row.state)
                        }}</span
                      >
                    </td>
                    <td
                      v-if="isLogisticsDetail"
                      data-label="Distribuidor"
                      class="ops-detail-wide"
                    >
                      <div
                        v-if="report.canEditLogistics"
                        class="ops-inline-edit"
                      >
                        <select
                          v-model="drafts[row.id]"
                          :aria-label="'Distribuidor de ' + row.trackingNumber"
                          :disabled="saving === row.id"
                          ><option value="">Sin asignar</option
                          ><option
                            v-for="d in distributors"
                            :key="d.id"
                            :value="String(d.id)"
                            >{{ d.name }}</option
                          ></select
                        ><button
                          class="ops-button"
                          :disabled="
                            saving === row.id ||
                              String(row.distributorId || '') === drafts[row.id]
                          "
                          @click="saveDistributor(row)"
                        >
                          {{ saving === row.id ? 'Guardando…' : 'Guardar' }}
                        </button>
                      </div>
                      <span v-else>{{
                        distributorName(row.distributorId)
                      }}</span>
                    </td>
                    <td v-else data-label="Días hábiles">
                      <span
                        :class="[
                          'ops-days',
                          { 'ops-days--late': row.days > 3 },
                        ]"
                        >{{ row.days === null ? 'Sin fecha' : row.days }}</span
                      >
                    </td>
                    <td
                      v-if="detail.section === 'pending'"
                      data-label="Responsable del pendiente"
                      class="ops-detail-wide"
                    >
                      <div v-if="report.canEdit" class="ops-inline-edit">
                        <select
                          v-model="drafts[row.pendingId]"
                          :aria-label="
                            'Responsable de ' +
                              row.invoiceNumber +
                              ': ' +
                              row.reason
                          "
                          :disabled="saving === row.pendingId"
                          ><option value="">Sin asignar</option
                          ><option>Distribuidor</option
                          ><option>Laboratorio</option></select
                        ><button
                          class="ops-button"
                          :disabled="
                            saving === row.pendingId ||
                              (row.responsibleParty || '') ===
                                drafts[row.pendingId]
                          "
                          @click="savePending(row)"
                        >
                          {{
                            saving === row.pendingId ? 'Guardando…' : 'Guardar'
                          }}
                        </button>
                      </div>
                      <span v-else>{{
                        row.responsibleParty || 'Sin asignar'
                      }}</span>
                    </td>
                  </tr>
                  <tr v-if="!detail.items.length">
                    <td
                      :colspan="detail.section === 'pending' ? 5 : 4"
                      class="ops-empty"
                    >
                      No hay registros para esta selección.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="ops-pagination ops-detail-footer">
            <span class="ops-detail-range">{{ detailRange }}</span>
            <div v-if="detail.total > 20" class="ops-pagination-controls">
              <button
                class="ops-button"
                :disabled="detail.page === 1 || detailLoading"
                @click="changePage(-1)"
              >
                Anterior</button
              ><span
                >Página {{ detail.page }} de
                {{ Math.ceil(detail.total / 20) }}</span
              ><button
                class="ops-button"
                :disabled="detail.page * 20 >= detail.total || detailLoading"
                @click="changePage(1)"
              >
                Siguiente
              </button>
            </div>
            <button
              v-else
              class="ops-button"
              :disabled="saving !== null"
              @click="closeList"
            >
              Cerrar
            </button>
          </div>
        </section>
      </v-dialog>
      <p class="ops-data-note">
        Pendientes al corte: incluye {{ number(report.carried) }} trámites de
        meses anteriores.<span v-if="report.missingDates">
          {{ report.missingDates }} trámites sin fechas válidas quedan fuera del
          cálculo del SLA.</span
        >
      </p>
    </template>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import Dashboard from '@/api/Dashboard'
import SessionHelper from '@/helpers/SessionHelper'

const now = () => moment().tz('America/Panama')
export default {
  name: 'OperationsDashboard',
  data() {
    return {
      filters: { distId: '', month: now().month() + 1, year: now().year() },
      months: [
        'Enero',
        'Febrero',
        'Marzo',
        'Abril',
        'Mayo',
        'Junio',
        'Julio',
        'Agosto',
        'Septiembre',
        'Octubre',
        'Noviembre',
        'Diciembre',
      ],
      report: null,
      distributors: [],
      loading: false,
      error: '',
      exporting: false,
      exportError: '',
      requestId: 0,
      detail: null,
      detailLoading: false,
      detailError: '',
      detailRequest: 0,
      drafts: {},
      saving: null,
      saveError: '',
      showAlerts: false,
      teamSearch: '',
      teamPage: 1,
      teamPageSize: 5,
      teamSort: 'processing',
      teamDirection: 'desc',
      teamColumns: [
        { key: 'name', label: 'Colaborador' },
        { key: 'processing', label: 'En proceso' },
        { key: 'pending', label: 'Pendientes' },
        { key: 'approved', label: 'Aprobados' },
        { key: 'denied', label: 'Rechazados' },
        { key: 'delivered', label: 'Entregas' },
        { key: 'overdue', label: 'Fuera de SLA' },
      ],
      timer: null,
      socketTimer: null,
      trigger: null,
      metrics: [
        {
          key: 'received',
          title: 'Trámites para procesar',
          description: 'Ingresados a TRANSIT en el mes',
          icon: 'description',
          tone: 'blue',
        },
        {
          key: 'processing',
          title: 'Trámites en proceso',
          description: 'Pendientes de aprobación o rechazo al corte',
          icon: 'assignment',
          tone: 'orange',
        },
        {
          key: 'approved',
          title: 'Trámites aprobados',
          description: 'Aprobados durante el mes',
          icon: 'check_circle',
          tone: 'green',
        },
        {
          key: 'denied',
          title: 'Trámites rechazados',
          description: 'Rechazados durante el mes',
          icon: 'cancel',
          tone: 'red',
        },
        {
          key: 'overdue',
          title: 'Trámites fuera de SLA',
          description: 'Superaron los 3 días hábiles',
          icon: 'schedule',
          tone: 'purple',
        },
      ],
    }
  },
  computed: {
    years() {
      return Array.from(
        { length: now().year() - 1998 },
        (_, i) => now().year() + 1 - i
      )
    },
    periodLabel() {
      return this.report
        ? this.months[this.report.period.month - 1] +
            ' ' +
            this.report.period.year
        : ''
    },
    canOpenInvoice() {
      return ['GEM', 'ADM'].includes(SessionHelper.role)
    },
    filteredTeam() {
      const normalize = text =>
        text
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLocaleLowerCase('es')
      const term = normalize(this.teamSearch.trim())
      return (this.report ? this.report.team : []).filter(u =>
        normalize(u.name + ' ' + u.role).includes(term)
      )
    },
    sortedTeam() {
      const direction = this.teamDirection === 'asc' ? 1 : -1
      return this.filteredTeam.slice().sort((a, b) => {
        const comparison =
          this.teamSort === 'name'
            ? a.name.localeCompare(b.name, 'es', {
                sensitivity: 'base',
                numeric: true,
              })
            : Number(a[this.teamSort] || 0) - Number(b[this.teamSort] || 0)
        return (
          direction * comparison ||
          a.name.localeCompare(b.name, 'es', {
            sensitivity: 'base',
            numeric: true,
          }) ||
          a.id - b.id
        )
      })
    },
    teamPages() {
      return Math.max(
        1,
        Math.ceil(this.filteredTeam.length / this.teamPageSize)
      )
    },
    pagedTeam() {
      return this.sortedTeam.slice(
        (this.teamPage - 1) * this.teamPageSize,
        this.teamPage * this.teamPageSize
      )
    },
    teamRange() {
      if (!this.filteredTeam.length) return '0 colaboradores'
      return `Mostrando ${(this.teamPage - 1) * this.teamPageSize +
        1}–${Math.min(
        this.teamPage * this.teamPageSize,
        this.filteredTeam.length
      )} de ${this.filteredTeam.length}`
    },
    detailRange() {
      if (!this.detail || this.detailLoading) return 'Cargando registros…'
      if (!this.detail.total) return 'Sin registros'
      return `Mostrando ${(this.detail.page - 1) * 20 + 1}–${Math.min(
        (this.detail.page - 1) * 20 + this.detail.items.length,
        this.detail.total
      )} de ${this.detail.total}`
    },
    isLogisticsDetail() {
      return (
        this.detail && /^(mensajero|transportista)-/.test(this.detail.section)
      )
    },
  },
  watch: {
    teamSearch() {
      this.teamPage = 1
    },
    teamPageSize() {
      this.teamPage = 1
    },
    teamSort() {
      this.teamPage = 1
      this.teamDirection = this.teamSort === 'name' ? 'asc' : 'desc'
    },
    teamDirection() {
      this.teamPage = 1
    },
    teamPages(value) {
      this.teamPage = Math.min(this.teamPage, value)
    },
  },
  created() {
    this.load()
    this.timer = setInterval(() => {
      if (!document.hidden && !this.loading && !this.saving) this.load(true)
    }, 60000)
    this.joinTracking()
  },
  beforeDestroy() {
    clearInterval(this.timer)
    clearTimeout(this.socketTimer)
    this.requestId += 1
    this.detailRequest += 1
  },
  methods: {
    async downloadReport() {
      if (!this.report || this.loading || this.exporting || this.error) return
      // Snapshot the successfully loaded scope, independently of team search/page.
      const { month, year, distId } = this.report.period
      this.exporting = true
      this.exportError = ''
      try {
        const blob = await Dashboard.downloadOperationsReport({
          month,
          year,
          distId: distId || '',
        })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `informe-operaciones-${year}-${String(month).padStart(
          2,
          '0'
        )}-${distId || 'todos'}.pdf`
        document.body.appendChild(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      } catch (e) {
        this.exportError =
          'No se pudo descargar el informe PDF. Comprueba la conexión y vuelve a intentar.'
      } finally {
        this.exporting = false
      }
    },
    sortTeam(key) {
      if (this.teamSort === key)
        this.teamDirection = this.teamDirection === 'desc' ? 'asc' : 'desc'
      else this.teamSort = key
    },
    onDetailVisibility(visible) {
      if (!visible) this.closeList()
    },
    number(value) {
      return Number(value || 0).toLocaleString('es-PA')
    },
    initials(name) {
      return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(n => n[0])
        .join('')
    },
    formatDate(value, time = false) {
      return value
        ? moment
            .tz(value, 'America/Panama')
            .format(time ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY')
        : '—'
    },
    serviceTitle(type) {
      return type === 'mensajero' ? 'Mensajería' : 'Transportista'
    },
    distributorName(id) {
      const d = this.distributors.find(item => Number(item.id) === Number(id))
      return d ? d.name : 'Sin asignar'
    },
    stateLabel(state) {
      return (
        {
          RECEIVED: 'Para procesar',
          REINIT: 'Reingreso',
          FD_ADMISSION: 'En proceso',
          APPROVED: 'Aprobado',
          DENIED: 'Rechazado',
          DELIVERED: 'Entregado',
          pendiente: 'Pendiente',
          asignado: 'Asignado',
          en_transito: 'En camino',
          llegada: 'En punto de entrega',
          evidencia: 'Registrando evidencia',
          firma: 'Firma registrada',
          entregado: 'Entregado',
        }[state] || state
      )
    },
    barWidth(total, rows) {
      return (total / Math.max(1, ...rows.map(r => r.total))) * 100 + '%'
    },
    alertText(a) {
      return a.kind === 'delivered'
        ? `${this.serviceTitle(a.assignmentType)}: entrega confirmada · ${
            a.trackingNumber
          }`
        : `Alerta preventiva de SLA · Trámite ${a.invoiceNumber ||
            '#' + a.invoiceId}`
    },
    changeFilters() {
      this.exportError = ''
      this.closeList()
      this.teamPage = 1
      this.load()
    },
    async load(background = false) {
      const id = ++this.requestId
      this.loading = true
      if (!background) this.error = ''
      try {
        const data = await Dashboard.getOperations({ ...this.filters })
        if (id !== this.requestId) return
        if (!data || !data.totals || !data.period)
          throw new Error('Respuesta inválida')
        this.report = data
        this.distributors = data.distributors
        this.error = ''
      } catch (e) {
        if (id === this.requestId)
          this.error = this.report
            ? 'No se pudo actualizar el dashboard. Se conserva el último resultado; revisa el período mostrado y vuelve a intentar.'
            : 'No se pudo cargar el dashboard. Comprueba la conexión y vuelve a intentar.'
      } finally {
        if (id === this.requestId) this.loading = false
      }
    },
    async openList(section, title, extra = {}) {
      this.trigger = document.activeElement
      this.detail = { section, title, ...extra, items: [], total: 0, page: 1 }
      this.saveError = ''
      this.drafts = {}
      this.detailLoading = true
      await this.$nextTick()
      if (this.$refs.detailClose)
        this.$refs.detailClose.focus({ preventScroll: true })
      this.loadDetail()
    },
    closeList() {
      if (this.saving !== null) return
      this.detailRequest += 1
      this.detail = null
      this.saveError = ''
      const trigger = this.trigger
      this.$nextTick(() => {
        if (trigger && document.contains(trigger))
          trigger.focus({ preventScroll: true })
      })
    },
    async loadDetail() {
      if (!this.detail) return
      const id = ++this.detailRequest
      this.detailLoading = true
      this.detailError = ''
      try {
        const data = await Dashboard.getOperations({
          month: this.report.period.month,
          year: this.report.period.year,
          distId: this.report.period.distId || '',
          section: this.detail.section,
          responsible: this.detail.responsible,
          reasonId: this.detail.reasonId,
          page: this.detail.page,
        })
        if (id !== this.detailRequest || !this.detail) return
        this.detail.items = data.items
        this.detail.total = data.total
        data.items.forEach(row => {
          this.$set(
            this.drafts,
            row.pendingId || row.id,
            this.isLogisticsDetail
              ? String(row.distributorId || '')
              : row.responsibleParty || ''
          )
        })
      } catch (e) {
        if (id === this.detailRequest)
          this.detailError = 'No se pudo cargar la lista.'
      } finally {
        if (id === this.detailRequest) this.detailLoading = false
      }
    },
    changePage(delta) {
      this.detail.page += delta
      this.loadDetail()
    },
    async savePending(row) {
      this.saving = row.pendingId
      this.saveError = ''
      try {
        const result = await Dashboard.assignPending(
          row.pendingId,
          this.drafts[row.pendingId] || null
        )
        row.responsibleParty = result.responsibleParty
        if (
          this.detail &&
          this.detail.responsible &&
          this.detail.responsible !== (row.responsibleParty || 'Sin asignar')
        ) {
          this.detail.items = this.detail.items.filter(
            item => item.pendingId !== row.pendingId
          )
          this.detail.total -= 1
        }
        await this.load(true)
      } catch (e) {
        this.saveError =
          'No se pudo guardar el responsable. Tu selección se conserva; vuelve a pulsar Guardar.'
      } finally {
        this.saving = null
      }
    },
    async saveDistributor(row) {
      this.saving = row.id
      this.saveError = ''
      try {
        const result = await Dashboard.assignDistributor(
          row.id,
          this.drafts[row.id] ? Number(this.drafts[row.id]) : null
        )
        row.distributorId = result.distributorId
        if (
          this.detail &&
          this.report.period.distId &&
          Number(row.distributorId) !== Number(this.report.period.distId)
        ) {
          this.detail.items = this.detail.items.filter(
            item => item.id !== row.id
          )
          this.detail.total -= 1
        }
        await this.load(true)
      } catch (e) {
        this.saveError =
          'No se pudo guardar el distribuidor. Tu selección se conserva; vuelve a pulsar Guardar.'
      } finally {
        this.saving = null
      }
    },
    joinTracking() {
      if (this.$socket && ['GEM', 'ADM'].includes(SessionHelper.role))
        this.$socket.emit('join_admin_tracking', { token: SessionHelper.token })
    },
  },
  sockets: {
    connect() {
      this.joinTracking()
    },
    'shipment:status_changed'(event) {
      if (event.currentStatus === 'entregado')
        this.$toast.success(
          `Entrega confirmada: ${event.trackingNumber || event.shipmentId}`
        )
      clearTimeout(this.socketTimer)
      this.socketTimer = setTimeout(() => this.load(true), 1000)
    },
  },
}
</script>

<style scoped src="./operations-dashboard.css"></style>
<style>
.ops-modal-shell.v-dialog {
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 20px 60px rgba(15, 30, 55, 0.22);
}
.ops-modal-shell.v-dialog--fullscreen {
  border-radius: 0;
}
@media (prefers-reduced-motion: reduce) {
  .ops-modal-shell.v-dialog {
    transition: none !important;
  }
}
</style>
